import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import FallartenAuswahl from '@/components/FallartenAuswahl';
import {
  trackHeroCtaClick,
  trackWebsiteBridgeClick,
  trackAppLandingStartCta,
  captureEntrySource,
} from '@/lib/tracking';
import { useState } from 'react';
import { useEffect } from 'react';
import {
  TreeDeciduous,
  ArrowRight,
  CheckCircle2,
  Mail,
  ExternalLink,
  Search,
  Eye,
  BarChart3,
  UserPlus,
  Unlock,
  FileText,
  ClipboardCheck,
  Users,
  Menu,
  X,
  ShieldCheck,
  Lock,
} from 'lucide-react';

const LandingPage = () => {
  const { t } = useLanguage();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // UTM-Parameter beim Einstieg erfassen
  useEffect(() => {
    captureEntrySource();
  }, []);

  return (
    <div className="min-h-screen bg-warm-50" data-testid="landing-page">
      {/* ── HEADER ── */}
      <header className="sticky top-0 z-50 bg-white border-b border-warm-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-primary">
                <TreeDeciduous className="h-5 w-5 text-white" />
              </div>
              <div className="flex flex-col">
                <span className="font-serif text-xl font-bold text-primary leading-tight">ForestHub</span>
                <span className="text-[10px] text-muted-foreground tracking-wide leading-tight" data-testid="header-slogan">{t('landing.headerSlogan')}</span>
              </div>
            </Link>

            <nav className="hidden md:flex items-center gap-6">
              <Link
                to="/schnell-check"
                className="text-primary font-medium hover:text-primary/80 transition-colors"
                data-testid="nav-schnellcheck"
              >
                {t('landing.schnellCheckLink')}
              </Link>
            </nav>

            <div className="flex items-center gap-3">
              <Link to="/anmelden" className="hidden sm:block">
                <Button variant="ghost" className="text-gray-600 hover:text-primary" data-testid="landing-login-btn">
                  {t('nav.login')}
                </Button>
              </Link>
              <Link to="/registrieren" className="hidden sm:block">
                <Button className="bg-primary hover:bg-primary-600 text-white" data-testid="landing-register-btn">
                  {t('nav.register')}
                </Button>
              </Link>

              {/* Mobile Hamburger */}
              <button
                className="md:hidden p-2 rounded-lg hover:bg-warm-100 transition-colors"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-btn"
                aria-label="Menü öffnen"
              >
                {mobileMenuOpen ? <X className="h-5 w-5 text-gray-700" /> : <Menu className="h-5 w-5 text-gray-700" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-warm-200 bg-white" data-testid="mobile-menu">
            <nav className="max-w-7xl mx-auto px-4 py-4 space-y-1">
              <Link
                to="/schnell-check"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-primary hover:bg-primary/5 transition-colors"
                data-testid="mobile-nav-schnellcheck"
              >
                <Search className="h-4 w-4" />
                {t('landing.schnellCheckLink')}
              </Link>
              <div className="border-t border-warm-200 my-2" />
              <Link
                to="/anmelden"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-700 hover:bg-warm-100 transition-colors"
                data-testid="mobile-nav-login"
              >
                {t('nav.login')}
              </Link>
              <Link
                to="/registrieren"
                onClick={() => setMobileMenuOpen(false)}
                className="block"
                data-testid="mobile-nav-register"
              >
                <Button className="w-full bg-primary hover:bg-primary-600 text-white">
                  {t('nav.register')}
                </Button>
              </Link>

            </nav>
          </div>
        )}
      </header>

      {/* ── HERO — ruhig, textbasiert, sachlich ── */}
      <section className="bg-warm-50 border-b border-warm-200 py-16 sm:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <p className="text-primary/70 text-sm font-medium tracking-widest uppercase mb-4" data-testid="hero-tagline">
            {t('landing.heroTagline')}
          </p>
          <h1
            className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 leading-[1.15] tracking-tight mb-4"
            data-testid="hero-headline"
          >
            {t('landing.heroHeadline')}
          </h1>
          <p
            className="text-lg sm:text-xl text-gray-700 mb-3 leading-relaxed font-medium"
            data-testid="hero-subline"
          >
            {t('landing.heroSubline')}
          </p>
          <p
            className="text-base text-gray-500 mb-10 leading-relaxed max-w-2xl"
            data-testid="hero-desc"
          >
            {t('landing.heroDesc')}
          </p>

          <Link
            to="/schnell-check"
            onClick={() => { trackHeroCtaClick('hero_schnellcheck'); trackAppLandingStartCta('hero_primary'); }}
            data-testid="hero-schnellcheck-btn"
          >
            <Button
              size="lg"
              className="bg-primary text-white hover:bg-primary/90 h-13 px-10 text-base shadow-md font-semibold"
            >
              {t('landing.heroCta')}
            </Button>
          </Link>

          <p className="text-gray-400 text-sm mt-4" data-testid="hero-hint">
            {t('landing.heroHint')}
          </p>
        </div>
      </section>

      {/* ── TRUST BAR ── */}
      <section className="py-6 bg-white border-b border-warm-200" data-testid="trust-bar">
        <div className="max-w-5xl mx-auto px-4">
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-3 text-sm text-gray-600">
            {[t('landing.trust1'), t('landing.trust2'), t('landing.trust3'), t('landing.trust4')].map((text, i) => (
              <div key={i} className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                <span>{text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FALLARTEN-AUSWAHL ── */}
      <FallartenAuswahl />

      {/* ── HOW IT WORKS – 4 STEPS ── */}
      <section className="py-20 bg-white" data-testid="vier-schritte">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-14">
            <h2 className="font-serif text-2xl md:text-3xl font-bold text-gray-900 mb-3">
              {t('landing.howTitle')}
            </h2>
            <p className="text-gray-500 max-w-xl mx-auto text-sm sm:text-base">
              {t('landing.howSubtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 md:gap-5">
            {[
              { icon: Search, step: '1', title: t('landing.howS1Title'), desc: t('landing.howS1Desc') },
              { icon: Eye, step: '2', title: t('landing.howS2Title'), desc: t('landing.howS2Desc') },
              { icon: BarChart3, step: '3', title: t('landing.howS3Title'), desc: t('landing.howS3Desc') },
            ].map((item, i) => (
              <div key={i} className="text-center md:text-left" data-testid={`step-${item.step}`}>
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-primary/8 text-primary mb-4">
                  <item.icon className="h-6 w-6" />
                </div>
                <div className="text-xs font-semibold text-primary/60 uppercase tracking-wide mb-1.5">
                  {t('common.step')} {item.step}
                </div>
                <h3 className="font-semibold text-gray-900 mb-2 text-base">{item.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/schnell-check" onClick={() => trackHeroCtaClick('section_schnellcheck')} data-testid="section-schnellcheck-btn">
              <Button className="h-12 px-8 text-base font-medium">
                <Search className="h-5 w-5 mr-2" />
                {t('landing.howCta')}
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ── WALDRECHNER BRIDGE ── */}
      <section className="py-6 bg-primary/5 border-b border-primary/10" data-testid="waldrechner-bridge">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <p className="text-sm text-primary font-medium">
            {t('landing.bridgeHint')}
          </p>
        </div>
      </section>

      {/* ── TEIL A: MUSTER-ERGEBNIS VORSCHAU ── */}
      <section className="py-20 bg-white" data-testid="preview-section">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="font-serif text-2xl md:text-3xl font-bold text-gray-900 mb-3">
              {t('landing.previewTitle')}
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-sm sm:text-base leading-relaxed">
              {t('landing.previewDesc')}
            </p>
            <p className="text-gray-500 max-w-2xl mx-auto text-sm mt-2 leading-relaxed">
              {t('landing.previewDesc2')}
            </p>
            <p className="text-primary/80 max-w-xl mx-auto text-sm mt-3 leading-relaxed font-medium" data-testid="ergebnis-fokus">
              {t('landing.ergebnisFokus')}
            </p>
          </div>

          <div className="max-w-2xl mx-auto">
            <Card className="border-warm-200 bg-warm-50/50 shadow-sm overflow-hidden">
              <div className="bg-primary/5 border-b border-warm-200 px-5 py-3 flex items-center gap-2">
                <FileText className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium text-gray-700">{t('landing.previewCardTitle')}</span>
              </div>
              <CardContent className="pt-5 pb-6 px-5 space-y-5">
                {/* Kurzfazit */}
                <div>
                  <p className="text-xs font-semibold text-primary uppercase tracking-wide mb-1.5">{t('landing.previewKurzfazit')}</p>
                  <p className="text-sm text-gray-700 leading-relaxed bg-white rounded-lg border border-warm-200 p-3">
                    {t('landing.previewKurzfazitText')}
                  </p>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  {/* Offene Punkte */}
                  <div>
                    <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-1.5">{t('landing.previewOffenePunkte')}</p>
                    <ul className="space-y-1.5">
                      {[t('landing.previewOP1'), t('landing.previewOP2')].map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                          <span className="text-amber-500 mt-0.5 shrink-0">&#9679;</span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Stolpersteine */}
                  <div>
                    <p className="text-xs font-semibold text-red-700 uppercase tracking-wide mb-1.5">{t('landing.previewStolpersteine')}</p>
                    <ul className="space-y-1.5">
                      {[t('landing.previewSS1'), t('landing.previewSS2')].map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                          <span className="text-red-400 mt-0.5 shrink-0">&#9679;</span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Nächste Schritte */}
                <div>
                  <p className="text-xs font-semibold text-primary uppercase tracking-wide mb-1.5">{t('landing.previewNaechsteSchritte')}</p>
                  <ul className="space-y-1.5">
                    {[t('landing.previewNS1'), t('landing.previewNS2')].map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                        <ArrowRight className="h-3.5 w-3.5 text-primary mt-0.5 shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </CardContent>
            </Card>
            <p className="text-xs text-gray-400 text-center mt-3 italic">{t('landing.previewHinweis')}</p>
          </div>
        </div>
      </section>

      {/* ── PRICING / 2 TIERS ── */}
      <section className="py-20 bg-warm-50" data-testid="tiers-section">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-14">
            <h2 className="font-serif text-2xl md:text-3xl font-bold text-gray-900 mb-3">
              {t('landing.tiersTitle')}
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto text-sm sm:text-base">
              {t('landing.tiersSubtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {/* Schnell-Check */}
            <Card className="border-warm-200 bg-white relative" data-testid="tier-a-card">
              <CardContent className="pt-7 pb-6 px-6">
                <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-primary/8 rounded-full text-primary text-xs font-semibold mb-4">
                  <Unlock className="h-3.5 w-3.5" />
                  {t('landing.tierABadge')}
                </div>
                <h3 className="font-serif text-lg font-bold text-gray-900 mb-1">{t('landing.tierATitle')}</h3>
                <p className="text-2xl font-bold text-primary mb-5">{t('landing.tierAPrice')}</p>
                <ul className="space-y-2.5 mb-6">
                  {[t('landing.tierAF1'), t('landing.tierAF2'), t('landing.tierAF3'), t('landing.tierAF4')].map((f, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-gray-600">
                      <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                <Link to="/schnell-check" onClick={() => trackHeroCtaClick('tier_a_cta')}>
                  <Button variant="outline" className="w-full border-primary text-primary hover:bg-primary/5" data-testid="tier-a-cta">
                    <Search className="h-4 w-4 mr-2" />
                    {t('landing.tierACta')}
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Fallklärung */}
            <Card className="border-primary/30 bg-white relative ring-1 ring-primary/15" data-testid="tier-c-card">
              <CardContent className="pt-7 pb-6 px-6">
                <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-primary/10 rounded-full text-primary text-xs font-semibold mb-4">
                  <FileText className="h-3.5 w-3.5" />
                  {t('landing.tierCBadge')}
                </div>
                <h3 className="font-serif text-lg font-bold text-gray-900 mb-1">{t('landing.tierCTitle')}</h3>
                <p className="text-2xl font-bold text-primary mb-5">{t('landing.tierCPrice')}</p>
                <ul className="space-y-2.5 mb-6">
                  {[t('landing.tierCF1'), t('landing.tierCF2'), t('landing.tierCF3'), t('landing.tierCF4'), t('landing.tierCF5')].map((f, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-gray-600">
                      <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                <Link to="/schnell-check" onClick={() => trackHeroCtaClick('tier_c_cta')}>
                  <Button className="w-full bg-primary hover:bg-primary/90 text-white" data-testid="tier-c-cta">
                    {t('landing.tierCCta')}
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>

          {/* TEIL C: Kaufmoment-Kontrast */}
          <div className="mt-12 max-w-3xl mx-auto" data-testid="kauf-kontrast">
            <h3 className="font-serif text-lg md:text-xl font-bold text-gray-900 text-center mb-6">
              {t('landing.kaufKontrastTitle')}
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-warm-50 border border-warm-200 rounded-xl p-5">
                <p className="text-sm font-semibold text-gray-700 mb-3">{t('landing.kaufKontrastFree')}</p>
                <ul className="space-y-2">
                  {[t('landing.kaufKontrastFreeF1'), t('landing.kaufKontrastFreeF2'), t('landing.kaufKontrastFreeF3')].map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-500">
                      <CheckCircle2 className="h-4 w-4 text-gray-400 mt-0.5 shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-primary/[0.04] border border-primary/20 rounded-xl p-5">
                <p className="text-sm font-semibold text-primary mb-3">{t('landing.kaufKontrastPaid')}</p>
                <ul className="space-y-2">
                  {[t('landing.kaufKontrastPaidF1'), t('landing.kaufKontrastPaidF2'), t('landing.kaufKontrastPaidF3'), t('landing.kaufKontrastPaidF4')].map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                      <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <p className="text-sm text-gray-500 text-center mt-5 leading-relaxed max-w-xl mx-auto">
              {t('landing.kaufKontrastHinweis')}
            </p>
          </div>

          {/* Leistungsgrenzen */}
          <div className="mt-10 text-center max-w-2xl mx-auto" data-testid="tier-disclaimer">
            <p className="text-sm font-semibold text-gray-700 mb-1.5">{t('landing.tierFollowTitle')}</p>
            <p className="text-sm text-gray-500 leading-relaxed">
              {t('landing.tierFollowDesc')}
            </p>
            <p className="text-xs text-gray-400 mt-3 italic">
              {t('landing.tierDisclaimer')}
            </p>
          </div>
        </div>
      </section>

      {/* ── REGISTRATION & COST EXPLANATION ── */}
      <section className="py-20 bg-white" data-testid="reg-section">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-serif text-2xl md:text-3xl font-bold text-gray-900 mb-3">
              {t('landing.regTitle')}
            </h2>
            <p className="text-gray-500 max-w-xl mx-auto text-sm sm:text-base">
              {t('landing.regSubtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Free */}
            <div className="text-center md:text-left" data-testid="reg-free">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-green-50 text-green-700 mb-4">
                <Unlock className="h-6 w-6" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2 text-base">{t('landing.regFreeTitle')}</h3>
              <p className="text-sm text-gray-500 leading-relaxed">{t('landing.regFreeDesc')}</p>
            </div>
            {/* Registration */}
            <div className="text-center md:text-left" data-testid="reg-login">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-blue-50 text-blue-700 mb-4">
                <UserPlus className="h-6 w-6" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2 text-base">{t('landing.regLoginTitle')}</h3>
              <p className="text-sm text-gray-500 leading-relaxed">{t('landing.regLoginDesc')}</p>
            </div>
            {/* Paid */}
            <div className="text-center md:text-left" data-testid="reg-paid">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-primary/8 text-primary mb-4">
                <ClipboardCheck className="h-6 w-6" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2 text-base">{t('landing.regPaidTitle')}</h3>
              <p className="text-sm text-gray-500 leading-relaxed">{t('landing.regPaidDesc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── POSITIONING ── */}
      <section className="py-20 bg-warm-50" data-testid="positionierung">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-serif text-2xl md:text-3xl font-bold text-gray-900 mb-3">
              {t('landing.posTitle')}
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto text-sm sm:text-base">
              {t('landing.posSubtitle')}
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            {[
              { strike: t('landing.pos1Strike'), better: t('landing.pos1Better'), desc: t('landing.pos1Desc') },
              { strike: t('landing.pos2Strike'), better: t('landing.pos2Better'), desc: t('landing.pos2Desc') },
              { strike: t('landing.pos3Strike'), better: t('landing.pos3Better'), desc: t('landing.pos3Desc') },
              { strike: t('landing.pos4Strike'), better: t('landing.pos4Better'), desc: t('landing.pos4Desc') },
            ].map((item, i) => (
              <Card key={i} className="border-warm-200 bg-white" data-testid={`pos-card-${i}`}>
                <CardContent className="pt-6 pb-5">
                  <p className="text-sm text-gray-400 line-through mb-1">{item.strike}</p>
                  <p className="text-sm font-semibold text-primary mb-2">{item.better}</p>
                  <p className="text-sm text-gray-600 leading-relaxed">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ── FOR WHOM – 2 TARGET GROUPS ── */}
      <section className="py-20 bg-white" data-testid="fuer-wen">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-14">
            <h2 className="font-serif text-2xl md:text-3xl font-bold text-gray-900 mb-3">
              {t('landing.fuerWenTitle')}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {/* Waldbesitzer */}
            <Card className="bg-primary/[0.03] border-primary/15" data-testid="target-waldbesitzer">
              <CardContent className="p-6">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-primary/10 rounded-full text-primary text-sm font-medium mb-4">
                  <TreeDeciduous className="h-4 w-4" />
                  {t('landing.ownersLabel')}
                </div>
                <h3 className="font-serif text-lg font-bold text-gray-900 mb-3">
                  {t('landing.ownersTitle')}
                </h3>
                <ul className="space-y-2 mb-5">
                  {[t('landing.ownersF1'), t('landing.ownersF2'), t('landing.ownersF3'), t('landing.ownersF4')].map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                      <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
                <Link to="/schnell-check" data-testid="owners-cta">
                  <Button size="sm" className="bg-primary hover:bg-primary-600 text-white w-full">
                    {t('landing.ownersCta')}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Erben / Unsicher */}
            <Card className="bg-amber-500/[0.03] border-amber-500/15" data-testid="target-erben">
              <CardContent className="p-6">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-amber-50 rounded-full text-amber-700 text-sm font-medium mb-4">
                  <Users className="h-4 w-4" />
                  {t('landing.heirsLabel')}
                </div>
                <h3 className="font-serif text-lg font-bold text-gray-900 mb-3">
                  {t('landing.heirsTitle')}
                </h3>
                <ul className="space-y-2 mb-5">
                  {[t('landing.heirsF1'), t('landing.heirsF2'), t('landing.heirsF3'), t('landing.heirsF4')].map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                      <CheckCircle2 className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
                <Link to="/schnell-check" data-testid="heirs-cta">
                  <Button size="sm" variant="outline" className="border-amber-600 text-amber-700 hover:bg-amber-50 w-full">
                    {t('landing.heirsCta')}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* ── TEIL B: VERTRAUENSBLOCK ── */}
      <section className="py-14 bg-warm-50" data-testid="trust-block">
        <div className="max-w-2xl mx-auto px-4">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
              <ShieldCheck className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h3 className="font-serif text-lg font-bold text-gray-900 mb-2">{t('landing.trustBlockTitle')}</h3>
              <p className="text-sm text-gray-600 leading-relaxed mb-2">
                {t('landing.trustBlockText1')} {t('landing.trustBlockText2')}
              </p>
              <p className="text-sm text-gray-600 leading-relaxed mb-3">
                ForestHub ersetzt keine Fachberatung. Es hilft, Ihren Fall vorher zu ordnen, damit Gespräche mit Forststelle, Berater oder Käufer klarer und schneller werden.
              </p>
              <p className="text-sm text-gray-500 leading-relaxed flex items-start gap-2">
                <Lock className="h-3.5 w-3.5 text-gray-400 mt-0.5 shrink-0" />
                Ihre Daten werden nicht an Makler, Holzhändler oder Werbepartner weitergegeben. Sie dienen ausschließlich der Einordnung Ihres Falls.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── WECHSELHILFE ── */}
      <section className="py-10 bg-white border-t border-warm-200" data-testid="wechselhilfe">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <p className="text-sm font-medium text-gray-700 mb-2">{t('landing.wechselTitle')}</p>
          <p className="text-xs text-gray-500 leading-relaxed mb-4">{t('landing.wechselText')}</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a href="https://waldrechner.eu" target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:text-primary/80 transition-colors underline underline-offset-2" data-testid="link-waldrechner">
              {t('landing.wechselWaldrechner')}
            </a>
            <span className="hidden sm:inline text-gray-300">·</span>
            <a href="https://foresthub.app" target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:text-primary/80 transition-colors underline underline-offset-2" data-testid="link-foresthub">
              {t('landing.wechselForesthub')}
            </a>
          </div>
        </div>
      </section>

      {/* ── ABGRENZUNG ── */}
      <section className="py-8 bg-warm-50 border-t border-warm-200" data-testid="abgrenzung">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <p className="text-xs text-gray-400 leading-relaxed">{t('landing.abgrenzungText')}</p>
          <p className="text-xs text-gray-400 leading-relaxed mt-2">{t('landing.dachHinweis')}</p>
        </div>
      </section>

      {/* ── TRUST + PARTNER ── */}
      <section className="py-8 bg-warm-50 border-y border-warm-200" data-testid="partner-bar">
        <div className="max-w-5xl mx-auto px-4">
          <div className="flex flex-wrap items-center justify-center gap-8 text-sm text-gray-400">
            <span>{t('landing.connectedPlatforms')}:</span>
            <a href="https://foresthub.app" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors" onClick={trackWebsiteBridgeClick}>foresthub.app</a>
            <a href="https://waldrechner.eu" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">waldrechner.eu</a>
            <a href="https://skogskalkyl.se" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">skogskalkyl.se</a>
            <a href="https://hodema.se" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">hodema.se</a>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="bg-primary text-white py-12" data-testid="landing-footer">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <TreeDeciduous className="h-6 w-6" />
                <span className="font-serif text-xl font-bold">ForestHub</span>
              </div>
              <p className="text-white/60 text-sm leading-relaxed">
                {t('landing.footerDesc')}
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-3">{t('landing.contact')}</h4>
              <a href="mailto:info@hodema.se" className="flex items-center gap-2 text-white/60 hover:text-white transition-colors text-sm">
                <Mail className="h-4 w-4" />
                info@hodema.se
              </a>
              <a href="https://foresthub.app" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-white/60 hover:text-white transition-colors text-sm mt-2">
                <ExternalLink className="h-4 w-4" />
                foresthub.app
              </a>
            </div>
            <div>
              <h4 className="font-semibold mb-3">{t('landing.linksTitle')}</h4>
              <div className="flex flex-col gap-2 text-sm text-white/60">
                <Link to="/schnell-check" className="hover:text-white transition-colors">{t('landing.schnellCheckLink')}</Link>
                <Link to="/anmelden" className="hover:text-white transition-colors">{t('nav.login')}</Link>
                <Link to="/datenschutz" className="hover:text-white transition-colors">{t('footer.privacy')}</Link>
                <Link to="/impressum" className="hover:text-white transition-colors">{t('footer.imprint')}</Link>
                <Link to="/agb" className="hover:text-white transition-colors">{t('footer.terms')}</Link>
              </div>
            </div>
          </div>
          <div className="border-t border-white/20 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-white/60 text-sm">
              &copy; {new Date().getFullYear()} ForestHub &middot; {t('landing.projectBy')} HODEMA edertools AB
            </div>
            <div className="text-white/60 text-sm">
              {t('footer.rights')}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
